import { WindowAddress } from '@cutr/constants/windows/windows';
import { createContext, ReactNode, useContext, useState } from 'react';

export type WindowAddressState = Pick<
  WindowAddress,
  'name' | 'phone' | 'line1' | 'line2' | 'city' | 'postalCode' | 'country'
>;

type ContextType = {
  address: WindowAddressState;
  setProp: (key: keyof WindowAddressState, value: string) => void;
  init: (address: WindowAddressState) => void;
  reset: () => void;
};

const Context = createContext<ContextType | null>(null);

const DEFAULT_ADDRESS: WindowAddressState = {
  name: '',
  phone: '',
  line1: '',
  line2: '',
  postalCode: '',
  city: '',
  country: '',
};

export const useDeliveryAddress = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'useDeliveryAddressContext must be used within a DeliveryAddressProvider'
    );
  }
  return context;
};

export const DeliveryAddressProvider = ({
  initialAddress,
  children,
}: {
  initialAddress?: WindowAddressState;
  children: ReactNode;
}) => {
  const [address, setAddress] = useState<WindowAddressState>(
    initialAddress || DEFAULT_ADDRESS
  );

  const setProp = (key: keyof WindowAddressState, value: string) => {
    setAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const init = (newAddress: WindowAddressState) => {
    setAddress(newAddress);
  };

  const reset = () => {
    setAddress(DEFAULT_ADDRESS);
  };

  return (
    <Context.Provider value={{ address, setProp, init, reset }}>
      {children}
    </Context.Provider>
  );
};

export const useAddressPretty = () => {
  const { address } = useDeliveryAddress();

  const addressPretty = [
    address.line1,
    address.line2,
    address.postalCode,
    address.city,
  ]
    .filter(Boolean)
    .join(', ');

  return {
    name: address.name,
    address: addressPretty,
    countryName: address.country,
    tel: address.phone,
  };
};
